import { useState, useEffect, useCallback } from 'react';
import { Capacitor } from '@capacitor/core';
import { NativeBiometric } from '@capgo/capacitor-native-biometric';

const isNative = Capacitor.isNativePlatform();
const CREDENTIAL_SERVER = 'track.lalatendu.info';
const BIOMETRIC_KEY = 'biometric_enabled';

interface BiometricState {
  isAvailable: boolean;
  isEnabled: boolean;
  biometryType: string;
  isNative: boolean;
  error: string | null;
}

export function useBiometric() {
  const [state, setState] = useState<BiometricState>({
    isAvailable: false,
    isEnabled: localStorage.getItem(BIOMETRIC_KEY) === 'true',
    biometryType: 'none',
    isNative,
    error: null,
  });

  useEffect(() => {
    if (!isNative) return;
    (async () => {
      try {
        const result = await NativeBiometric.isAvailable();
        setState(prev => ({
          ...prev,
          isAvailable: result.isAvailable,
          biometryType: result.biometryType === 1 ? 'fingerprint' :
                        result.biometryType === 2 ? 'face' :
                        result.biometryType === 3 ? 'iris' : 'none',
        }));
      } catch {
        setState(prev => ({ ...prev, isAvailable: false }));
      }
    })();
  }, []);

  const enableBiometric = useCallback(async (email: string, password: string) => {
    if (!isNative) return false;
    try {
      await NativeBiometric.setCredentials({
        username: email,
        password,
        server: CREDENTIAL_SERVER,
      });
      localStorage.setItem(BIOMETRIC_KEY, 'true');
      setState(prev => ({ ...prev, isEnabled: true, error: null }));
      return true;
    } catch {
      setState(prev => ({ ...prev, error: 'Failed to enable biometric login' }));
      return false;
    }
  }, []);

  const authenticateWithBiometric = useCallback(async (): Promise<{ email: string; password: string } | null> => {
    if (!isNative) return null;
    try {
      await NativeBiometric.verifyIdentity({
        reason: 'Log in to Bus Tracker',
        title: 'Biometric Login',
        subtitle: 'Use fingerprint or face to sign in',
        negativeButtonText: 'Use Password',
      });
      const credentials = await NativeBiometric.getCredentials({
        server: CREDENTIAL_SERVER,
      });
      return { email: credentials.username, password: credentials.password };
    } catch {
      setState(prev => ({ ...prev, error: null }));
      return null;
    }
  }, []);

  const disableBiometric = useCallback(async () => {
    if (isNative) {
      try {
        await NativeBiometric.deleteCredentials({ server: CREDENTIAL_SERVER });
      } catch { /* ignore */ }
    }
    localStorage.removeItem(BIOMETRIC_KEY);
    setState(prev => ({ ...prev, isEnabled: false, error: null }));
  }, []);

  return {
    ...state,
    enableBiometric,
    authenticateWithBiometric,
    disableBiometric,
  };
}
